import { useEffect, useMemo, useState } from 'react';
import type { TPersona } from '@newo/api/gateway';
import { runtimeConfig } from '@/const/env';
import { useProfileQuery } from '@/services/customer/profile/profile.queries';
import { useAccountQuery } from '@/services/customer/account/account.queries';
import { useVibeBuilderConfigQuery } from '@/services/integration/integration.queries';
import { useFindPersonaByName } from '@/services/bff/bff.queries';
import { useCreatePersonaMutation } from '@/services/customer/personas/personas.queries';
import useAppStore from '@/store/app';
import {
    buildVibePersonaName,
    loadVibeBuilderComponent,
    resolveVibePersona
} from '@/hooks/useVibeBuilderRemote.utils';
import useVibeModeStore from '@/modules/VibeMode/VibeMode.store';
import type { TVibeBuilderComponent } from '@/hooks/useVibeBuilderRemote.utils';

export const useVibeBuilderRemote = () => {
    const isAuthorized = useAppStore((state) => state.isAuthorized);

    const [Component, setComponent] = useState<
        TVibeBuilderComponent | undefined
    >();
    const [persona, setPersona] = useState<TPersona | undefined>();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<Error | undefined>();

    const {
        data: vibeBuilderConfig,
        isSuccess: isConfigSuccess,
        isError: isConfigError
    } = useVibeBuilderConfigQuery(isAuthorized);

    const isConfigSettled = isConfigSuccess || isConfigError;
    const isFeatureEnabled = Boolean(vibeBuilderConfig?.clientSecret);

    const { data: account, isPending: isAccountPending } = useAccountQuery();
    const { data: profile, isPending: isProfilePending } =
        useProfileQuery(isAuthorized);

    const customerIdn = profile?.idn;
    const email = account?.email;

    const personaName = useMemo(() => {
        if (!isFeatureEnabled || !email || !customerIdn) {
            return;
        }

        return buildVibePersonaName({ email, customerIdn });
    }, [customerIdn, email, isFeatureEnabled]);

    const { mutateAsync: findPersona } = useFindPersonaByName();
    const { mutateAsync: createPersona } = useCreatePersonaMutation();

    const retryToken = useVibeModeStore((state) => state.retryToken);

    useEffect(() => {
        if (isConfigSettled && !isFeatureEnabled) {
            setIsLoading(false);
            return;
        }

        if (
            !isFeatureEnabled ||
            isAccountPending ||
            isProfilePending ||
            !personaName
        ) {
            setIsLoading(true);
            return;
        }

        let cancelled = false;
        // Read through a getter so each check re-evaluates the captured flag.
        const isCancelled = () => cancelled;

        const bootstrap = async () => {
            setIsLoading(true);
            setError(undefined);

            try {
                const resolvedPersona = await resolveVibePersona({
                    personaName,
                    findPersona,
                    createPersona
                });

                if (isCancelled()) {
                    return;
                }

                if (!resolvedPersona) {
                    setError(
                        new Error(
                            `Vibe persona "${personaName}" could not be resolved`
                        )
                    );
                    setIsLoading(false);
                    return;
                }

                setPersona(resolvedPersona);

                const remoteSourceUrl = runtimeConfig.vibeBuilderRemoteUrl;

                if (remoteSourceUrl) {
                    const RemoteComponent =
                        await loadVibeBuilderComponent(remoteSourceUrl);

                    if (isCancelled()) {
                        return;
                    }

                    if (RemoteComponent) {
                        setComponent(() => RemoteComponent);
                    }
                }

                setIsLoading(false);
            } catch (bootstrapError) {
                if (isCancelled()) {
                    return;
                }

                setError(
                    bootstrapError instanceof Error
                        ? bootstrapError
                        : new Error(String(bootstrapError))
                );
                setIsLoading(false);
            }
        };

        void bootstrap();

        return () => {
            cancelled = true;
        };
    }, [
        createPersona,
        findPersona,
        isAccountPending,
        isConfigSettled,
        isFeatureEnabled,
        isProfilePending,
        personaName,
        retryToken
    ]);

    return {
        persona,
        Component,
        vibeBuilderConfig,
        isLoading,
        error,
        customerIdn
    };
};
